import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, ShoppingCart, X, Trash2, FileText, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { API_BASE_URL, apiService } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { NavigationMenu } from '@/components/dashboard/navigation-menu';
import { ScrollingAnnouncementBar } from '@/components/dashboard/scrolling-announcement-bar';
import { SessionDisplay } from '@/components/session/sessions';

interface Session {
  id: string;
  name: string;
  drawTime: string;
  active: boolean;
  date: string;
}

interface BetItem {
  number: number;
  qty: number;
  amount: number;
  sessionId: string;
  category: string;
}

interface GroupData {
  group: string;
  inputs: string[];
  qty: number;
  amt: number;
  result: string;
}

// Session configuration
const SESSION_CONFIG = [
  { label: 'Dear Morning', value: 'MOR', time: '13:00', display: 'MOR (13:00)' },
  { label: 'Dear Day', value: 'DAY', time: '18:00', display: 'DAY (18:00)' },
  { label: 'Dear Evening', value: 'EVE', time: '20:00', display: 'EVE (20:00)' },
  { label: 'Dear Night', value: 'NIGHT', time: '22:00', display: 'NIGHT (22:00)' }
];

export default function AddSingleLottery() {
  const [session, setSession] = useState<Session | null>(null);
  const { user, isAdmin } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [balance, setBalance] = useState(0);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [selectedGameTime, setSelectedGameTime] = useState('');
  const [groups, setGroups] = useState<GroupData[]>([
    { group: "DEAR-1st", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
    { group: "DEAR-2nd", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
    { group: "DEAR-3rd", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
    { group: "DEAR-4th", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
    { group: "DEAR-5th Last", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
    { group: "All", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchActiveSession();
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeSession) {
      setSelectedSessionId(activeSession.id);
      setSelectedGameTime(`${activeSession.name} (${activeSession.drawTime})`);
    }
  }, [activeSession]);

  const fetchActiveSession = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/sessions/active`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch active session');
      const sessionData: Session = await response.json();
      setSession(sessionData);
      setActiveSession(sessionData);
    } catch (err) {
      setError('Failed to load active session');
      console.error('Error fetching session:', err);
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Get active session
      const sessionResponse = await apiService.getActiveSession();
      if (sessionResponse.success && sessionResponse.data) {
        setActiveSession(sessionResponse.data);
      }

      // Get user balance
      const balanceResponse = await apiService.getBalance();
      if (balanceResponse.success && balanceResponse.data) {
        setBalance(balanceResponse.data.balance);
      }

      // Get all sessions (for admin)
      if (isAdmin) {
        const sessionsResponse = await apiService.getAllSessions();
        if (sessionsResponse.success && sessionsResponse.data) {
          setSessions(sessionsResponse.data);
        }
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionById = async (sessionId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch session');
      const sessionData: Session = await response.json();
      setSession(sessionData);
      setSelectedGameTime(`${sessionData.name} (${sessionData.drawTime})`);
    } catch (err) {
      setError('Failed to load session');
      console.error('Error fetching session:', err);
    }
  };

  const handleSessionChange = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    if (sessionId) {
      fetchSessionById(sessionId);
    } else {
      setSession(null);
      setSelectedGameTime('');
    }
  };

  const getSessionDisplayName = (session: Session) => {
    const config = SESSION_CONFIG.find(s => s.value === session.name);
    return config ? config.display : `${session.name} (${session.drawTime})`;
  };

  const navigateSessions = (direction: 'prev' | 'next') => {
    if (sessions.length === 0) return;

    const currentIndex = sessions.findIndex(s => s.id === selectedSessionId);
    let newIndex;

    if (direction === 'next') {
      newIndex = currentIndex < sessions.length - 1 ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : sessions.length - 1;
    }

    handleSessionChange(sessions[newIndex].id);
  };

  const refreshSession = () => {
    if (selectedSessionId) {
      fetchSessionById(selectedSessionId);
    } else {
      fetchActiveSession();
    }
  };

  const handleInputChange = (groupIndex: number, numberIndex: number, value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    
    setGroups(prev => {
      const newGroups = [...prev];
      newGroups[groupIndex].inputs[numberIndex] = numericValue;
      
      let totalQty = 0;
      let totalAmt = 0;
      
      newGroups[groupIndex].inputs.forEach(input => {
        const amount = parseInt(input) || 0;
        if (amount > 0) {
          totalQty++;
          totalAmt += amount;
        }
      });
      
      newGroups[groupIndex].qty = totalQty;
      newGroups[groupIndex].amt = totalAmt;
      
      return newGroups;
    });
  };

  const placeBets = async () => {
    if (!session) {
      setError('No active session found');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    const token = localStorage.getItem('auth_token');
    const bets: BetItem[] = [];

    groups.forEach(groupData => {
      groupData.inputs.forEach((amount, numberIndex) => {
        const numericAmount = parseInt(amount) || 0;
        if (numericAmount > 0) {
          bets.push({
            number: numberIndex,
            qty: 1,
            amount: numericAmount,
            sessionId: session.id,
            category: groupData.group.replace('DEAR-', '').replace(' Last', '')
          });
        }
      });
    });

    if (bets.length === 0) {
      setError('Please enter at least one bet amount');
      setLoading(false);
      return;
    }

    for (const bet of bets) {
      try {
        const response = await fetch(`${API_BASE_URL}/bookings/single`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bet),
        });

        const result = await response.json();

        if (!response.ok) {
          if (result.error === 'Insufficient balance') {
            setError(`Insufficient balance for bet: Number ${bet.number}, Amount ${bet.amount}`);
            break;
          } else {
            throw new Error(result.error || 'Failed to place bet');
          }
        }
      } catch (err) {
        setError(`Failed to place bet for number ${bet.number}: ${err}`);
        break;
      }
    }

    if (!error) {
      setSuccess('All bets placed successfully!');
      setGroups(groups.map(group => ({
        ...group,
        inputs: Array(10).fill(""),
        qty: 0,
        amt: 0
      })));
    }

    setLoading(false);
  };

  const clearAll = () => {
    setGroups(groups.map(group => ({
      ...group,
      inputs: Array(10).fill(""),
      qty: 0,
      amt: 0
    })));
    setError(null);
    setSuccess(null);
  };

  const cancelBet = () => {
    clearAll();
    setSuccess('Bet cancelled');
  };

  return (
    <div className="container mx-auto max-w-6xl">
      <header className="bg-gradient-to-r from-red-800 to-red-900 text-white py-3 px-4 sm:px-6 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-4">
          <NavigationMenu
            isMobileMenuOpen={isMobileMenuOpen} 
            setIsMobileMenuOpen={setIsMobileMenuOpen} 
            isAdmin={isAdmin}
          />
        </div>
      </header>

      {/* Scrolling Announcement Bar */}
      <ScrollingAnnouncementBar 
        text={`🎯 Welcome to Three Circle 7 Lottery • Active Session: ${activeSession?.name || 'None'} • Balance: ₹${balance} • Play Responsibly • Good Luck! •`} 
      />

      {/* Session Display - Below ScrollingAnnouncementBar */}
      <div className="my-4">
        <SessionDisplay session={activeSession} />
      </div>

      <Card className="mb-6">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Dear Single</CardTitle>
          
          {/* Session Selection Dropdown and Navigation */}
          <div className="flex flex-col items-center justify-center gap-4 mt-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateSessions('prev')}
                disabled={sessions.length === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <Select value={selectedSessionId} onValueChange={handleSessionChange}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select session...">
                    {selectedGameTime || 'Select session...'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {sessions.map((session) => (
                    <SelectItem key={session.id} value={session.id}>
                      {getSessionDisplayName(session)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateSessions('next')}
                disabled={sessions.length === 0}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

            </div>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshSession}
                disabled={loading}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="text-left">
              <p className="font-semibold">Rajahree Punjab</p>
              <p className="text-sm text-muted-foreground">Booking - Choice</p>
            </div>
            <div className="text-right">
              <p className="font-semibold">{selectedGameTime || 'No session selected'}</p>
              {session && (
                <p className="text-sm text-muted-foreground">
                  Draw: {new Date(session.drawTime).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 border-green-200">
          <AlertCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Betting Grid */}
      <Card>
        <CardContent className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="border p-2 bg-muted/50 font-semibold">Group</th>
                  {Array.from({ length: 10 }, (_, i) => (
                    <th key={i} className="border p-2 bg-muted/50 font-semibold text-center">
                      {i}
                    </th>
                  ))}
                  <th className="border p-2 bg-muted/50 font-semibold text-center">Gty</th>
                  <th className="border p-2 bg-muted/50 font-semibold text-center">Amt</th>
                  <th className="border p-2 bg-muted/50 font-semibold text-center">Result</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((groupData, groupIndex) => (
                  <tr key={groupData.group}>
                    <td className="border p-2 bg-muted/50 font-medium">{groupData.group}</td>
                    {groupData.inputs.map((value, numberIndex) => (
                      <td key={numberIndex} className="border p-1">
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={value}
                          onChange={(e) => handleInputChange(groupIndex, numberIndex, e.target.value)}
                          className="w-16 h-8 text-center"
                          placeholder="0"
                        />
                      </td>
                    ))}
                    <td className="border p-2 text-center font-medium">{groupData.qty}</td>
                    <td className="border p-2 text-center font-medium">{groupData.amt}</td>
                    <td className="border p-2 text-center font-medium">{groupData.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mt-6 justify-center">
            <Button 
              onClick={placeBets} 
              disabled={loading || !session}
              className="bg-green-600 hover:bg-green-700"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              {loading ? 'Placing Bets...' : 'BUY (F4)'}
            </Button>
            
            <Button 
              onClick={clearAll} 
              variant="outline"
              disabled={loading}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              CLEAR (F6)
            </Button>
            
            <Button 
              onClick={cancelBet} 
              variant="outline"
              disabled={loading}
              className="text-red-600 border-red-600 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-2" />
              CANCEL (F7)
            </Button>
            
            <Button variant="outline" disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />
              Single Result (Ctrl+H)
            </Button>
            
            <Button variant="outline" disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />
              Single Report (Ctrl+R)
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}